import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Box, TextField, PasswordField } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AppleButton } from '~/components/AppleButton/AppleButton';
import GoogleButton from '~/components/GoogleButton/GoogleButton';
import { IntuitButton } from '~/components/IntuitButton/IntuitButton';
import { routes, UtmParams } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useIsNativeAppleSignIn } from '~/hooks/useAppleSignIn';
import { useOnMount } from '~/hooks/useOnMount';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useValidationRules } from '~/hooks/useValidationRules/useValidationRules';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
import { FormSeparatorVertical } from '~/pages/unAuthorizedPages/components/FormSeparator';
import { passwordPolicyTextsPath, validatePassword } from '~/utils/password-policy';
import { FormContainer, FormTitle, FormFields, VerticalFormFields, HorizontalFormFields, SubmitButtonField, ErrorMessage, Footer, FooterText, FooterLink, FormSubTitle } from '../components/FormComponents';
import TermsAndPrivacyMessage from '../components/TermsAndPrivacyMessage';
import UnauthorizedPageContainer from '../components/UnauthorizedPageContainer';
import { XeroSignInButton } from '../components/XeroSignInButton/XeroSignInButton';
import { errorMessages } from '../constants';
import { LoginFlow } from '../LoginScreen/LoginScreen.types';
import OTPForm from '../LoginScreen/Steps/OTPForm';
import styles from './SignupScreen.module.scss';
export const GetSignupScreenTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            SignupScreenTID: {
                Form: 'signup-form',
                TryLoggingInLink: 'try-logging-in-link',
                SignInLink: 'sign-in-link',
                FirstNameInput: 'first-name-input',
                LastNameInput: 'last-name-input',
                EmailInput: 'email-input',
                PasswordInput: 'password-input',
                SignupButton: 'signup-button',
                AppleButton: 'apple-btn-sign-up',
                GoogleButton: 'google-btn-sign-up',
                ErrorMessage: 'error-msg'
            }
        }
    });
export function SignupForm(props) {
    const { onSubmitted, visitorSyncFrame, visitorSyncOrigin } = props;
    const { SignupScreenTID } = useMemo(()=>GetSignupScreenTID(), []);
    const { t: signupTexts } = useLocaleKeys('auth.screens.signup');
    const { t: pwValidationTexts } = useLocaleKeys(passwordPolicyTextsPath);
    const { emailValidation, nameValidation } = useValidationRules();
    const { logBI, api } = useStoreActions();
    const navigate = useNavigate();
    const { reportFormSubmission, reportToHubspot, prepareFormObject } = useEventReporter();
    const [signupInProgress, setSignupInProgress] = useState(false);
    const [signupError, setSignupError] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const getUtms = useUtmsParams();
    const { browser: { getOrigin } } = useIoContext();
    const { pathname } = useLocation();
    const nativeAppleSignIn = useIsNativeAppleSignIn();
    const { handleSubmit, control, formState, getValues, watch } = useForm({
        mode: 'onChange'
    });
    const passwordValue = watch('password', '');
    const userInputs = [
        getValues('email'),
        getValues('fullName.first'),
        getValues('fullName.last')
    ].filter(Boolean);
    const strengthScore = usePasswordStrength(passwordValue, userInputs);
    useOnMount(()=>{
        const utms = getUtms();
        if (UtmParams.Referral in utms) {
            logBI(18151, {
                referrer: utms[UtmParams.Referral]
            });
        }
    });
    const appleButton = /*#__PURE__*/ _jsx(AppleButton, {
        testId: SignupScreenTID.AppleButton(),
        promptForMissingName: true,
        onAuthError: (msg)=>{
            setErrorMessage(msg || errorMessages.generalSignupError);
            setSignupError(true);
        },
        onAuthSuccess: ()=>{
            navigate(routes.home);
        }
    });
    const googleButton = /*#__PURE__*/ _jsx(GoogleButton, {
        testId: SignupScreenTID.GoogleButton(),
        onAuthError: (msg)=>{
            setErrorMessage(msg || errorMessages.generalSignupError);
            setSignupError(true);
        },
        onAuthSuccess: ()=>{
            navigate(routes.home);
        }
    });
    const onSubmit = async (data)=>{
        const { fullName, email, password } = data;
        logBI(10206, {});
        setSignupInProgress(true);
        const utms = getUtms();
        if (UtmParams.Referral in utms) {
            utms[UtmParams.ReferralV2] = utms[UtmParams.Referral];
            delete utms[UtmParams.Referral];
        }
        const signup = await handlePromise(api.auth.signup({
            fullName,
            email,
            password,
            utms,
            pageUrl: getOrigin() + pathname
        }));
        if (!signup.success) {
            setSignupError(true);
            setSignupInProgress(false);
            const error = new ErrorResponse(signup.error);
            if (error.hasServerError(ServerError.EmailIsInvalid)) {
                setErrorMessage(errorMessages.noBusinessEmailError);
            } else if (error.hasServerError(ServerError.AccountAlreadyExists)) {
                setErrorMessage(/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        signupTexts.alreadyExistsError(),
                        ' ',
                        /*#__PURE__*/ _jsx(FooterLink, {
                            toInternalLink: routes.login,
                            underline: "always",
                            testId: SignupScreenTID.TryLoggingInLink(),
                            children: signupTexts.tryLoggingIn()
                        })
                    ]
                }));
            } else {
                setErrorMessage(errorMessages.generalSignupError);
            }
        } else {
            reportFormSubmission('app-sign-up', [
                prepareFormObject('email', email)
            ]); //TODO - replace this with specific events
            reportToHubspot('app_sign_up_', {
                email,
                firstname: fullName.first,
                lastname: fullName.last
            });
            setSignupInProgress(false);
            onSubmitted(email);
            visitorSyncOrigin !== '' && (visitorSyncFrame === null || visitorSyncFrame === void 0 ? void 0 : visitorSyncFrame.postMessage({
                type: 'anc_tracker_request',
                action: 'sync_user',
                data: {
                    userId: signup.data.id,
                    properties: {},
                    origin: window.location.origin,
                    path: window.location.pathname
                }
            }, "https://".concat(visitorSyncOrigin)));
        }
    };
    return /*#__PURE__*/ _jsxs(FormContainer, {
        id: "signup-form",
        onSubmit: handleSubmit(onSubmit),
        "data-testid": SignupScreenTID.Form(),
        onChange: ()=>setSignupError(false),
        children: [
            /*#__PURE__*/ _jsx(FormTitle, {
                children: signupTexts.signUp()
            }),
            /*#__PURE__*/ _jsxs(FormSubTitle, {
                children: [
                    /*#__PURE__*/ _jsxs(FooterText, {
                        children: [
                            " ",
                            signupTexts.alreadyHaveAccount(),
                            " "
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FooterLink, {
                        toInternalLink: routes.login,
                        testId: SignupScreenTID.SignInLink(),
                        children: signupTexts.logIn()
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FormFields, {
                children: /*#__PURE__*/ _jsxs(HorizontalFormFields, {
                    className: styles.formWrapper,
                    children: [
                        /*#__PURE__*/ _jsxs(VerticalFormFields, {
                            children: [
                                /*#__PURE__*/ _jsxs(HorizontalFormFields, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "fullName.first",
                                            defaultValue: "",
                                            rules: nameValidation(signupTexts.firstNameLabel(), {
                                                required: signupTexts.pleaseEnterFirstName()
                                            }),
                                            render: (param)=>{
                                                let { field, fieldState } = param;
                                                var _fieldState_error;
                                                return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                    bi: {
                                                        name: 'signup_first_name'
                                                    },
                                                    collapseHelperText: false
                                                }, field), {
                                                    label: signupTexts.firstNameLabel(),
                                                    error: fieldState.isTouched && !!fieldState.error,
                                                    helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                                    testId: SignupScreenTID.FirstNameInput()
                                                }));
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "fullName.last",
                                            defaultValue: "",
                                            rules: nameValidation(signupTexts.lastNameLabel(), {
                                                required: signupTexts.pleaseEnterLastName()
                                            }),
                                            render: (param)=>{
                                                let { field, fieldState } = param;
                                                var _fieldState_error;
                                                return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                    bi: {
                                                        name: 'signup_last_name'
                                                    },
                                                    collapseHelperText: false
                                                }, field), {
                                                    label: signupTexts.lastNameLabel(),
                                                    error: fieldState.isTouched && !!fieldState.error,
                                                    helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                                    testId: SignupScreenTID.LastNameInput()
                                                }));
                                            }
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "email",
                                    defaultValue: "",
                                    rules: emailValidation(undefined, {
                                        required: signupTexts.pleaseEnterEmail()
                                    }),
                                    render: (param)=>{
                                        let { field, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                            bi: {
                                                name: 'signup_email'
                                            },
                                            collapseHelperText: false
                                        }, field), {
                                            label: signupTexts.emailLabel(),
                                            type: "email",
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            testId: SignupScreenTID.EmailInput()
                                        }));
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "password",
                                    defaultValue: "",
                                    rules: _object_spread_props(_object_spread({}, requiredValidation(signupTexts.pleaseEnterPassword())), {
                                        validate: (value)=>validatePassword(value, [
                                                getValues('email'),
                                                getValues('fullName.first'),
                                                getValues('fullName.last')
                                            ].filter(Boolean), pwValidationTexts)
                                    }),
                                    render: (param)=>{
                                        let { field, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                            bi: {
                                                name: 'signup_password'
                                            }
                                        }, field), {
                                            onBlur: ()=>{
                                                logBI(10203, {
                                                    isPasswordValid: !fieldState.error
                                                });
                                                field.onBlur();
                                            },
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            showStrengthMeter: true,
                                            strengthScore: strengthScore,
                                            testId: SignupScreenTID.PasswordInput()
                                        }));
                                    }
                                }),
                                /*#__PURE__*/ _jsx(SubmitButtonField, {
                                    disabled: !formState.isValid,
                                    isLoading: signupInProgress,
                                    testId: SignupScreenTID.SignupButton(),
                                    children: signupTexts.continueButton()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(FormSeparatorVertical, {
                            children: signupTexts.orSeparator()
                        }),
                        /*#__PURE__*/ _jsxs(Box, {
                            className: styles.oauthButtonsContainer,
                            children: [
                                nativeAppleSignIn ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        appleButton,
                                        googleButton
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        googleButton,
                                        appleButton
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(XeroSignInButton, {
                                    mode: "signUp"
                                }),
                                /*#__PURE__*/ _jsx(IntuitButton, {
                                    mode: "signUp"
                                })
                            ]
                        })
                    ]
                })
            }),
            signupError && /*#__PURE__*/ _jsx(ErrorMessage, {
                testId: SignupScreenTID.ErrorMessage(),
                children: errorMessage
            }),
            /*#__PURE__*/ _jsx(Footer, {
                children: /*#__PURE__*/ _jsx(TermsAndPrivacyMessage, {
                    type: "signup"
                })
            })
        ]
    });
}
export function SignupScreen() {
    const [email, setEmail] = useState('');
    const [step, setStep] = useState('signup');
    const { t: signupTexts } = useLocaleKeys('auth.screens.signup');
    const visitorSyncFrame = useMemo(()=>{
        var _document_getElementById;
        return (_document_getElementById = document.getElementById('visitor-sync-frame')) === null || _document_getElementById === void 0 ? void 0 : _document_getElementById.contentWindow;
    }, []);
    useSetDocumentTitle(signupTexts.documentTitle());
    return /*#__PURE__*/ _jsxs(UnauthorizedPageContainer, {
        testId: step,
        wideLayout: step === 'signup',
        otpLayout: step === 'waiting-verification',
        children: [
            step === 'signup' && /*#__PURE__*/ _jsx(SignupForm, {
                onSubmitted: (email)=>{
                    if (email) {
                        setEmail(email);
                        setStep('waiting-verification');
                    }
                },
                visitorSyncFrame: visitorSyncFrame,
                visitorSyncOrigin: window._visitorSyncHostname || ''
            }),
            step === 'waiting-verification' && /*#__PURE__*/ _jsx(OTPForm, {
                email: email,
                flow: LoginFlow.EmailVerification,
                onBack: ()=>setStep('signup'),
                shouldRequestOTP: false
            })
        ]
    });
}
